import { getDefaultCountryId } from '~/data/geography'

/** In-flight seed so concurrent ensureSeeded() callers await one load. */
let seedInflight: Promise<void> | null = null

/**
 * Shared discovery location (country / state / city) used by the app-bar
 * pin and by Employees / Jobs / Companies search filters.
 *
 * Seeded once from the signed-up profile (seeker CV) or company record.
 */
export function useDiscoveryLocation() {
  const countryId = useState<number | null>('discovery-loc-country', () => null)
  const stateId = useState<number | null>('discovery-loc-state', () => null)
  const cityId = useState<number | null>('discovery-loc-city', () => null)
  const open = useState('discovery-loc-open', () => false)
  const seeded = useState('discovery-loc-seeded', () => false)
  /** Bumped whenever location is seeded / applied / cleared so search views sync. */
  const generation = useState('discovery-loc-generation', () => 0)

  // Capture Nuxt-bound composables during setup / sync call sites — never after
  // an await (guest jobs page seeds from onMounted and was throwing
  // "Nuxt instance was called outside…").
  const jobFilters = useJobFilters()

  const hasLocation = computed(() => countryId.value != null)

  function syncToJobFilters() {
    jobFilters.countryId.value = countryId.value
    jobFilters.stateId.value = stateId.value
    jobFilters.cityId.value = cityId.value
  }

  async function ensureSeeded() {
    if (seeded.value) return
    if (seedInflight) {
      await seedInflight
      return
    }

    // Resolve composables before the first await while Nuxt context is still live.
    const { account } = useProfileAccount()
    const cvEditor = useCvEditor()
    const companyApi = useCompany()

    seedInflight = (async () => {
      try {
        if (account.value === 'seeker') {
          await cvEditor.load()
          countryId.value = cvEditor.personal.value.country_id ?? (await getDefaultCountryId())
          stateId.value = cvEditor.personal.value.state_id ?? null
          cityId.value = cvEditor.personal.value.city_id ?? null
        }
        else {
          const company = await companyApi.get<{
            country_id?: number | null
            state_id?: number | null
            city_id?: number | null
          }>().catch(() => null)
          countryId.value = company?.country_id ?? (await getDefaultCountryId())
          stateId.value = company?.state_id ?? null
          cityId.value = company?.city_id ?? null
        }
      }
      catch {
        countryId.value = await getDefaultCountryId()
        stateId.value = null
        cityId.value = null
      }

      syncToJobFilters()
      seeded.value = true
      generation.value += 1
    })().finally(() => {
      seedInflight = null
    })

    await seedInflight
  }

  function setLocation(next: {
    countryId: number | null
    stateId: number | null
    cityId: number | null
  }) {
    countryId.value = next.countryId
    stateId.value = next.stateId
    cityId.value = next.cityId
    // Explicit pin from onboarding / the location sheet is authoritative —
    // don't let a later ensureSeeded() overwrite it with a stale empty seed.
    seeded.value = true
    syncToJobFilters()
    generation.value += 1
  }

  function clear() {
    setLocation({ countryId: null, stateId: null, cityId: null })
  }

  /** Drop the pin so the next session re-seeds from the new account. */
  function reset() {
    countryId.value = null
    stateId.value = null
    cityId.value = null
    seeded.value = false
    seedInflight = null
    syncToJobFilters()
    generation.value += 1
  }

  function openSheet() {
    open.value = true
  }

  function closeSheet() {
    open.value = false
  }

  return {
    countryId,
    stateId,
    cityId,
    open,
    generation,
    hasLocation,
    ensureSeeded,
    setLocation,
    clear,
    reset,
    openSheet,
    closeSheet,
  }
}
